import { useWindowVirtualizer } from "@tanstack/react-virtual";
import moment from "moment/min/moment-with-locales";
import React, { useCallback, useMemo, useRef, useState } from "react";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import {
    useFetchData,
    useLatestCalculatedDate,
    useResolvedRadioNames,
    useSetDaypartOnChange,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
} from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, { latest2Weeks, latestDay, latestMonth, latestQuarter, latestWeek } from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import MarketPicker from "../components/Pickers/MarketPicker";
import MinMaxSpinsPicker from "../components/Pickers/MinMaxSpinsPicker";
import MusicSelectionTypePicker from "../components/Pickers/MusicSelectionTypePicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import MusicChartHeader from "../components/musicChart/MusicChartHeader";
import MusicChartRow from "../components/musicChart/MusicChartRow";
import { DataContent, InputSelection, ModulePage, SearchWithDownloadRow, SoleDescriptionRow, VirtualizedWindowContainer } from "../components/pageSections";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";
import ReleaseYearChart from "../components/ReleaseYearChart";

const extraDefaults = { selectionType: "all", minSpins: 0, maxSpins: 0 };

export default function MusicCharts() {
    const moduleName = useMemo(() => "musicCharts", []);
    const moduleId = useMemo(() => ({ 0: 38, 1: 38, 2: 38 }), []);
    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const [submittedMarketName, setSubmittedMarketName] = useState("");
    const [filterData, setFilterData] = useState({ searchString: "" });
    const [resultCount, setResultCount] = useState(0);

    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();
    const resolvedRadios = useResolvedRadioNames();

    const tableContainerRef = useRef(null);
    const advancedInputRef = useRef(null);

    const predefinedRanges = useMemo(() => {
        const baseDate = latestCalculatedDate ?? undefined;
        if (formData.dateRange === undefined) {
            setFormData(current => ({ ...current, dateRange: latestWeek(baseDate, true) }));
        }
        return [latestDay(baseDate), latestWeek(baseDate), latest2Weeks(baseDate), latestMonth(baseDate), latestQuarter(baseDate)];
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    const demo = useMemo(() => [{ data_type: 0, listener_group: 0 }], []);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, demo);
    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    async function handleFormSubmit(event) {
        event.preventDefault();
        advancedInputRef.current.closeAdvanced();
        await saveFormData();
        await saveFormData(formData.station[0]?.radio_id ?? formData.market[0].market_id);
        // input parameters
        const id = formData.station[0]?.radio_id ?? formData.market[0].market_id;
        const chartType = formData.station.length ? "radio" : "market";
        const newQuery = formData.selectionType === "new" ? "new/" : "";
        const marketTypeQuery = formData.market.length ? `&market_type=${formData.market[0].type}` : "";
        const startDate = moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DD");
        const endDate = moment(formData.dateRange[1]).endOf("day").format("YYYY-MM-DD");
        const startTime = moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss");
        const endTime = moment()
            .hours(formData.hourRange[1] - 1)
            .minutes(59)
            .seconds(59)
            .format("HH:mm:ss");
        const limit = formData.selectionType === "top" ? 50 : 0;
        const url = `/charts/${chartType}/songs/${newQuery}?id=${id}${marketTypeQuery}&start_dt=${startDate}&end_dt=${endDate}&start_time=${startTime}&end_time=${endTime}&limit=${limit}&min_spins=${formData.minSpins}&max_spins=${formData.maxSpins}`;
        await fetchData(url, formData.days);
        setSubmittedMarketName(formData.market[0]?.market_name);
    }

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data.songs;
            setResultCount(filteredData.length);
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.artist.toLowerCase();
                    const title = x.title.toLowerCase();
                    const year = x.release_year?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || year === exact;
                            }

                            return artist.includes(term) || title.includes(term) || year.startsWith(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);
    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo, { key: "spins", direction: "descending" });

    useMemo(() => {
        if (sortedItems) {
            setResultCount(sortedItems.length);
        }
    }, [sortedItems]);

    const showDetailedSpins = useMemo(
        () => data && !data.market_id && moment(data.start_time, "HH:mm:ss").hours() === 0 && moment(data.end_time, "HH:mm:ss").hours() === 23,
        [data],
    );

    const csvHeaders = useMemo(() => {
        if (!data) return;
        let headers = ["Artist", "Title", "Release Year", "Spins", "First played", "Item ID"];
        if (data.market_id) headers = headers.concat(["First played on"]);
        else if (showDetailedSpins) headers = headers.concat(["18-6", "6-10", "10-14", "14-18"]);
        return headers;
    }, [data, showDetailedSpins]);

    const csvData = useMemo(() => {
        if (!sortedItems) return;
        return sortedItems.map(x => {
            let csvData = [x.artist, x.title, x.release_year, x.spins, moment(x.first_play).format("L HH:mm"), x.item_id];
            if (data.market_id) csvData = csvData.concat([resolvedRadios?.find(r => r.radio_id === x.first_on)?.radio_name ?? "Unknown"]);
            else if (showDetailedSpins) csvData = csvData.concat([x.spins_18_6, x.spins_6_10, x.spins_10_14, x.spins_14_18]);
            return csvData;
        });
    }, [data?.market_id, resolvedRadios, showDetailedSpins, sortedItems]);

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 32, []),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].item_id, [sortedItems]),
        overscan: 30,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    const memoizedChart = useMemo(() => {
        if (!data) return null;
        const endYear = moment(data.end_date).format("YYYY");

        return (
            <ReleaseYearChart
                fullData={data}
                data={data.songs}
                endYear={endYear}
                loading={loading}
                error={errorCode}
                submittedMarketName={submittedMarketName}
                moduleName={moduleName}
                isCompetitor={false}
            />
        );
    }, [data, submittedMarketName, loading, errorCode]);

    return (
        <ModulePage>
            <ModulePage.Title>Music Charts</ModulePage.Title>
            <ModulePage.InputSelection
                handleFormSubmit={handleFormSubmit}
                disabled={!(formData.station.length || formData.market.length) || !formData.dateRange || !formData.days.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={newData => {
                            setFormData({
                                ...newData,
                                market: [],
                            });
                        }}
                        name={moduleName}
                        includeCompetitors={true}
                        disabled={formData.market.length}
                        loadOnChange={loadFormData}
                        colXl={2}
                    />
                    <MarketPicker
                        formData={formData}
                        setFormData={newData => {
                            setFormData({
                                ...newData,
                                station: [],
                            });
                        }}
                        name={moduleName}
                        disabled={formData.station.length}
                        loadOnChange={loadFormData}
                        colXl={2}
                    />

                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MusicSelectionTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                </InputSelection.Advanced>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Music Charts"}
                        whatList={[
                            "See all the music played on any station",
                            "Filter by specific days or dayparts, or by type of song – new music, top 50, all songs",
                            "Different airplay and competitor markets are also available",
                        ]}
                        knowList={[
                            { text: "Filter for specific song titles, artists, or just words in the search bar above the table" },
                            { text: "Clicking on a song opens up a station by station breakdown" },
                            {
                                text: "Airplay and competitor market can be updated or adjust. Just reach out to",
                                url: "support@radioanalyzer.com",
                                title: "support@radioanalyzer.com",
                            },
                        ]}
                        relatedLinks={[
                            {
                                title: "What’s Goin’ On?",
                                url: "https://radioanalyzer.com/blog/what-s-goin-on",
                            },
                            {
                                title: "The Station Chart You Cannot Live Without",
                                url: "https://radioanalyzer.com/blog/the-station-chart-you-cannot-live-without",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/JBYks7BpLg4?si=oSP19JjXKpiYAuQ3"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        {data && <>{memoizedChart}</>}
                        <SoleDescriptionRow>
                            <h4 className="fw-bold text-uppercase mb-1 d-flex column-gap-1">Music Chart</h4>
                            <DataDescription>
                                {!!data?.market_id ? (
                                    <DataDescription.Simple label="Market" value={submittedMarketName} />
                                ) : (
                                    <DataDescription.Station radioId={data?.radio_id} />
                                )}
                                <DataDescription.DateRange startDate={data?.start_date} endDate={data?.end_date} />
                                <DataDescription.TimeRange startTime={data?.start_time} endTime={data?.end_time} />
                                <DataDescription.Perspective days={data?.days} />
                                <DataDescription.MarketRadios marketRadioIds={data?.radio_ids} />
                                <DataDescription.Simple label={"Results"} value={resultCount} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            csvHeaders={csvHeaders}
                            data={csvData}
                            filename={"Music Chart"}
                        />
                        <MusicChartHeader
                            sortConfig={sortConfig}
                            requestSort={requestSort}
                            showFirstOn={!!data?.market_id}
                            showDetailedSpins={showDetailedSpins}
                        />
                        <VirtualizedWindowContainer containerRef={tableContainerRef} virtualizer={virtualizer}>
                            {virtualizer.getVirtualItems().map(virtualRow => {
                                const item = sortedItems[virtualRow.index];
                                if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                                return (
                                    <MusicChartRow
                                        item={item}
                                        key={virtualRow.key}
                                        virtualRow={virtualRow}
                                        virtualizer={virtualizer}
                                        setCatalogueSong={setCatalogueSong}
                                        showDetailedSpins={showDetailedSpins}
                                        showFirstOn={!!data?.market_id}
                                    />
                                );
                            })}
                        </VirtualizedWindowContainer>
                    </>
                )}
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
